/**
 * Global i18n Localization
 * Supports English (en) and Chinese (zh)
 *
 * This file contains translations for global UI elements:
 * - Sidebar labels
 * - Slash command titles, descriptions, and group headings
 * - Common UI labels
 */

import type { Language } from '../types';

export type { Language };

// ---------------------------------------------------------------------------
// Translation shape
// ---------------------------------------------------------------------------

export interface GlobalTranslations {
  sidebar: {
    darkMode: string;
    lightMode: string;
    about: string;
    settings: string;
    signOut: string;
    language: string;
    newChat: string;
    searchPlaceholder: string;
  };
  slashCommands: {
    groupTitles: {
      features: string;
      collaboration: string;
      system: string;
    };
    loading: string;
    noResults: string;
    noCommands: string;
    /** Keyed by command id, e.g. "dashboards", "promo-links" */
    commands: Record<string, { title: string; description: string }>;
  };
  common: {
    loading: string;
    error: string;
    retry: string;
    cancel: string;
    confirm: string;
    save: string;
    delete: string;
    edit: string;
    search: string;
  };
  workspace: {
    sidebar: {
      title: string;
      allTasks: string;
      projects: string;
      newProject: string;
      noProjects: string;
      editProject: string;
      deleteProject: string;
      expandSidebar: string;
      collapseSidebar: string;
    };
    toolbar: {
      searchPlaceholder: string;
      newTask: string;
      closeWorkspace: string;
      showFilters: string;
      hideFilters: string;
      clearAll: string;
      listView: string;
      boardView: string;
    };
    filters: {
      allStatus: string;
      pending: string;
      inProgress: string;
      inReview: string;
      completed: string;
      onHold: string;
      blocked: string;
      cancelled: string;
      allPriority: string;
      urgent: string;
      high: string;
      medium: string;
      low: string;
      allDates: string;
      overdue: string;
      today: string;
      thisWeek: string;
      thisMonth: string;
    };
    groupBy: {
      status: string;
      priority: string;
      dueDate: string;
      created: string;
      assignee: string;
    };
    /** Task status display names (keyed by backend value) */
    statusLabels: Record<string, string>;
    /** Task priority display names (keyed by backend value) */
    priorityLabels: Record<string, string>;
    /** Task type display names (keyed by backend value) */
    typeLabels: Record<string, string>;
    /** Relative due date format labels */
    dateFormat: {
      today: string;
      tomorrow: string;
      /** Indexed by `Date.getDay()` — [Sun, Mon, Tue, Wed, Thu, Fri, Sat] */
      weekdays: string[];
      /** Template with `{n}`, e.g. "-{n} 天" / "-{n}d" */
      overdue: string;
      /** AM/PM labels for half-day precision */
      am: string;
      pm: string;
    };
    list: {
      loadingTasks: string;
      noTasks: string;
      noTasksMatch: string;
      tryAdjusting: string;
      createFirst: string;
      unassigned: string;
      noDueDate: string;
      later: string;
      yesterday: string;
      older: string;
    };
    board: {
      loadingBoard: string;
      noTasks: string;
      toDo: string;
      inProgress: string;
      review: string;
      done: string;
      unassigned: string;
    };
    drawer: {
      title: string;
      details: string;
      comments: string;
      description: string;
      information: string;
      project: string;
      type: string;
      priority: string;
      created: string;
      due: string;
      estimate: string;
      /** Suffix for day count, e.g. "天" / "d" */
      daysUnit: string;
      assigneeLabel: string;
      tags: string;
      overdueBadge: string;
      noComments: string;
      writeComment: string;
      commentBtn: string;
      sending: string;
      confirmDelete: string;
    };
    members: {
      title: string;          // "Members" / "成员" / "멤버"
      manageMembers: string;  // context-menu label
      invite: string;         // section heading
      inviteEmail: string;
      inviteRole: string;
      inviteSend: string;
      inviteSending: string;
      roleViewer: string;
      roleMember: string;
      roleManager: string;
      roleAdmin: string;
      roleOwner: string;
      list: string;           // "Members (N)"
      empty: string;
      you: string;
      remove: string;
      confirmRemove: string;
      changeRoleTitle: string;
      loadFailed: string;
      inviteSucceeded: string;  // supports {count} token
      inviteFailed: string;
    };
  };
  systemRegistry: {
    tabAgents: string;
    tabSkills: string;
    resyncButton: string;
    resyncing: string;
    columns: {
      id: string;
      name: string;
      type: string;
      status: string;
      roles: string;
      synced: string;
      actions: string;
    };
    statusActive: string;
    statusDeprecated: string;
    revokeAll: string;
    revoking: string;
    revokeConfirmTitle: string;
    revokeConfirmBody: string;   // template: {id}
    revokeConfirmAction: string;
    revokeCancel: string;
    emptyAgents: string;
    emptySkills: string;
    loading: string;
    loadFailed: string;
    syncFailed: string;
    revokeFailed: string;
  };
  chat: {
    disclaimer: string;
  };
}

// ---------------------------------------------------------------------------
// English translations
// ---------------------------------------------------------------------------

const en: GlobalTranslations = {
  sidebar: {
    darkMode: 'Dark Mode',
    lightMode: 'Light Mode',
    about: 'About',
    settings: 'Settings',
    signOut: 'Sign Out',
    language: 'Chinese',
    newChat: 'New Chat',
    searchPlaceholder: 'Search conversations...',
  },
  slashCommands: {
    groupTitles: {
      features: 'Features',
      collaboration: 'Tasks / Projects',
      system: 'System Management',
    },
    loading: 'Loading commands...',
    noResults: 'No matching commands',
    noCommands: 'No commands available',
    commands: {
      'dashboards': {
        title: 'Dashboards',
        description: 'Real-Time Dashboard',
      },
      'workspace': {
        title: 'Workspace',
        description: 'Unified Workspace',
      },
      'creative': {
        title: 'Creative Hub',
        description: 'Explore AI-powered tools for image, video, and text creation',
      },
      'permission': {
        title: 'Permission Management',
        description: 'Role and Permission Management (Admin Only)',
      },
      'finance': {
        title: 'Finance Dashboards',
        description: 'Finance analytics and Metabase dashboards',
      },
      'metabase': {
        title: 'Metabase',
        description: 'Metabase embedded reports',
      },
      'metabase-config': {
        title: 'Metabase Configuration',
        description: 'Manage report menus, links and change history',
      },
      'looker-studio': {
        title: 'Looker Studio Reports',
        description: 'View and explore Looker Studio embedded reports',
      },
      'cmi': {
        title: 'Customer & Market Intelligence',
        description: 'Customer insights platform based on real review data',
      },
      'promo-links': {
        title: 'Pinned Link Management',
        description: 'Create and manage VIVAIA pinned-product links',
      },
      'traffic-analytics': {
        title: 'Traffic Analytics',
        description: 'Traffic attribution analysis — multi-touch conversion paths, attribution modeling, budget optimization',
      },
      'parallax': {
        title: 'Parallax 推广视差',
        description: 'Parallax 推广视差 traffic analytics platform (opens in a new tab)',
      },
      'shopvista': {
        title: 'ShopVista Store Operations',
        description: 'Store performance and member analytics (opens in a new tab)',
      },
      'sonar': {
        title: 'Sonar 售后分析',
        description: 'Sonar 客服与售后分析（在新标签页打开）',
      },
      'prism': {
        title: 'Prism 运营棱镜',
        description: 'Prism 运营棱镜 KPI attribution analytics platform (opens in a new tab)',
      },
      'core-daily-report': {
        title: 'Core Daily Report 核心日报',
        description: 'Core daily report system (opens in a new tab)',
      },
      'ads-ga4-roi': {
        title: 'Ads × GA4 ROI 快照',
        description: 'Campaign-level Google Ads and GA4 ROI snapshot (opens in a new tab)',
      },
      'data-science-lab': {
        title: 'Data Science Lab',
        description: 'Internal data science collaboration platform — projects, data, notebooks, discussions',
      },
      'digital-asset-management': {
        title: 'Digital Asset Management',
        description: 'Manage enterprise digital assets (Company, Domain, Ad Account, etc.)',
      },
      'datacenter-config': {
        title: 'Datacenter Config',
        description: 'Datacenter configuration management',
      },
      'singapore-team': {
        title: 'Singapore Team Data',
        description: 'Singapore Offline Team Data Access',
      },
      'virtual-try-on': {
        title: 'Virtual Try-On',
        description: 'Virtual Try-On',
      },
      'youtube-video-analysis': {
        title: 'YouTube Analysis',
        description: 'YouTube Video Analysis',
      },
      'clear': {
        title: 'Clear Chat',
        description: 'Clear current chat messages',
      },
    },
  },
  common: {
    loading: 'Loading...',
    error: 'An error occurred',
    retry: 'Retry',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    search: 'Search',
  },
  workspace: {
    sidebar: {
      title: 'Workspace',
      allTasks: 'All Tasks',
      projects: 'Projects',
      newProject: 'New Project',
      noProjects: 'No projects yet',
      editProject: 'Edit Project',
      deleteProject: 'Delete Project',
      expandSidebar: 'Expand sidebar',
      collapseSidebar: 'Collapse sidebar',
    },
    toolbar: {
      searchPlaceholder: 'Search tasks...',
      newTask: 'New',
      closeWorkspace: 'Close workspace',
      showFilters: 'Show filters',
      hideFilters: 'Hide filters',
      clearAll: 'Clear all',
      listView: 'List view',
      boardView: 'Board view',
    },
    filters: {
      allStatus: 'All Status',
      pending: 'Pending',
      inProgress: 'In Progress',
      inReview: 'Acceptance',
      completed: 'Completed',
      onHold: 'On Hold',
      blocked: 'Blocked',
      cancelled: 'Cancelled',
      allPriority: 'All Priority',
      urgent: 'Urgent',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      allDates: 'All Dates',
      overdue: 'Overdue',
      today: 'Today',
      thisWeek: 'This Week',
      thisMonth: 'This Month',
    },
    groupBy: {
      status: 'Status',
      priority: 'Priority',
      dueDate: 'Due date',
      created: 'Created',
      assignee: 'Assignee',
    },
    statusLabels: {
      pending: 'Pending',
      in_progress: 'In Progress',
      in_review: 'Acceptance',
      on_hold: 'On Hold',
      blocked: 'Blocked',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
    priorityLabels: {
      urgent: 'Urgent',
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    },
    typeLabels: {
      feature: 'Feature',
      bug: 'Bug',
      improvement: 'Improvement',
      research: 'Research',
      documentation: 'Documentation',
      testing: 'Testing',
      deployment: 'Deployment',
      maintenance: 'Maintenance',
    },
    dateFormat: {
      today: 'Today',
      tomorrow: 'Tomorrow',
      weekdays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      overdue: '-{n}d',
      am: 'AM',
      pm: 'PM',
    },
    list: {
      loadingTasks: 'Loading tasks...',
      noTasks: 'No tasks yet',
      noTasksMatch: 'No tasks match filters',
      tryAdjusting: 'Try adjusting your search or filters',
      createFirst: 'Create your first task to get started',
      unassigned: 'Unassigned',
      noDueDate: 'No Due Date',
      later: 'Later',
      yesterday: 'Yesterday',
      older: 'Older',
    },
    board: {
      loadingBoard: 'Loading board...',
      noTasks: 'No tasks',
      toDo: 'To Do',
      inProgress: 'In Progress',
      review: 'Acceptance',
      done: 'Done',
      unassigned: 'Unassigned',
    },
    drawer: {
      title: 'Title',
      details: 'Details',
      comments: 'Comments',
      description: 'Description',
      information: 'Information',
      project: 'Project',
      type: 'Type',
      priority: 'Priority',
      created: 'Created',
      due: 'Due',
      estimate: 'Rough days estimate',
      daysUnit: 'd',
      assigneeLabel: 'Assignee',
      tags: 'Tags',
      overdueBadge: 'OVERDUE',
      noComments: 'No comments yet',
      writeComment: 'Write a comment...',
      commentBtn: 'Comment',
      sending: 'Sending...',
      confirmDelete: 'Confirm delete',
    },
    members: {
      title: 'Members',
      manageMembers: 'Manage members',
      invite: 'Invite',
      inviteEmail: 'Email address',
      inviteRole: 'Role',
      inviteSend: 'Send invitation',
      inviteSending: 'Sending…',
      roleViewer: 'Viewer',
      roleMember: 'Member',
      roleManager: 'Manager',
      roleAdmin: 'Admin',
      roleOwner: 'Owner',
      list: 'Members',
      empty: 'No members yet. Invite someone to collaborate.',
      you: 'you',
      remove: 'Remove',
      confirmRemove: 'Remove this member?',
      changeRoleTitle: 'Change role',
      loadFailed: 'Failed to load members',
      inviteSucceeded: 'Invitation sent',
      inviteFailed: 'Failed to send invitation',
    },
  },
  systemRegistry: {
    tabAgents: 'Agents & Tools',
    tabSkills: 'Skills',
    resyncButton: 'Re-sync all from registry',
    resyncing: 'Syncing...',
    columns: {
      id: 'ID',
      name: 'Name',
      type: 'Type',
      status: 'Status',
      roles: 'Roles',
      synced: 'Synced',
      actions: 'Actions',
    },
    statusActive: 'Active',
    statusDeprecated: 'Deprecated',
    revokeAll: 'Revoke all',
    revoking: 'Revoking...',
    revokeConfirmTitle: 'Revoke from all roles?',
    revokeConfirmBody: 'This removes {id} from every role that currently grants it. Cannot be undone from the UI.',
    revokeConfirmAction: 'Revoke all grants',
    revokeCancel: 'Cancel',
    emptyAgents: 'No agents registered.',
    emptySkills: 'No executable skills registered.',
    loading: 'Loading registry...',
    loadFailed: 'Failed to load registry',
    syncFailed: 'Sync failed',
    revokeFailed: 'Revoke failed',
  },
  chat: {
    disclaimer: 'Datacenter AI may produce inaccurate information — please double-check important results. For any questions, please contact the Data Center team.',
  },
};

// ---------------------------------------------------------------------------
// Chinese translations
// ---------------------------------------------------------------------------

const zh: GlobalTranslations = {
  sidebar: {
    darkMode: '深色模式',
    lightMode: '浅色模式',
    about: '关于',
    settings: '设置',
    signOut: '退出登录',
    language: 'English',
    newChat: '新建对话',
    searchPlaceholder: '搜索对话...',
  },
  slashCommands: {
    groupTitles: {
      features: '功能模块',
      collaboration: '任务/项目',
      system: '系统管理',
    },
    loading: '加载命令中...',
    noResults: '没有匹配的命令',
    noCommands: '暂无可用命令',
    commands: {
      'dashboards': {
        title: '数据看板',
        description: '实时数据仪表板',
      },
      'workspace': {
        title: '工作空间',
        description: '统一工作空间',
      },
      'creative': {
        title: '创意中心',
        description: '探索 AI 驱动的图像、视频和文本创作工具',
      },
      'permission': {
        title: '权限管理',
        description: '角色与权限管理 (仅管理员)',
      },
      'finance': {
        title: '财务看板',
        description: '财务分析和 Metabase 报表',
      },
      'metabase': {
        title: 'Metabase',
        description: 'Metabase 嵌入报表',
      },
      'metabase-config': {
        title: 'Metabase 配置',
        description: '管理报表菜单、链接配置与修改记录',
      },
      'looker-studio': {
        title: 'Looker Studio 报表',
        description: '查看和浏览 Looker Studio 嵌入报表',
      },
      'cmi': {
        title: '用户与市场洞察',
        description: '基于真实评论数据的用户洞察平台',
      },
      'promo-links': {
        title: '置顶链接管理',
        description: 'VIVAIA 置顶 SKU 链接创建与管理',
      },
      'traffic-analytics': {
        title: '流量分析',
        description: '流量归因分析平台 — 多触点转化路径、归因建模、预算优化',
      },
      'parallax': {
        title: 'Parallax 推广视差',
        description: 'Parallax 推广视差流量分析平台（在新标签页打开）',
      },
      'shopvista': {
        title: 'ShopVista 门店经营',
        description: '线下门店经营与会员分析（在新标签页打开）',
      },
      'sonar': {
        title: 'Sonar 售后分析',
        description: 'Sonar 客服与售后分析（在新标签页打开）',
      },
      'prism': {
        title: 'Prism 运营棱镜',
        description: 'Prism 运营棱镜 KPI 归因分析平台（在新标签页打开）',
      },
      'core-daily-report': {
        title: 'Core Daily Report 核心日报',
        description: '核心日报系统（在新标签页打开）',
      },
      'ads-ga4-roi': {
        title: 'Ads × GA4 ROI 快照',
        description: 'Campaign 级 Google Ads 与 GA4 ROI 快照（在新标签页打开）',
      },
      'data-science-lab': {
        title: '数据科学实验室',
        description: '内部数据科学协作平台 — 项目、数据、笔记本、讨论',
      },
      'digital-asset-management': {
        title: '数字资产管理',
        description: '管理企业数字资产 (公司、域名、广告账户等)',
      },
      'datacenter-config': {
        title: '数据中心配置',
        description: '数据中心配置管理',
      },
      'singapore-team': {
        title: '新加坡团队数据',
        description: '新加坡线下团队数据访问',
      },
      'virtual-try-on': {
        title: '虚拟试穿',
        description: '虚拟试穿',
      },
      'youtube-video-analysis': {
        title: 'YouTube 视频分析',
        description: 'YouTube 视频分析',
      },
      'clear': {
        title: '清空对话',
        description: '清空当前聊天消息',
      },
    },
  },
  common: {
    loading: '加载中...',
    error: '发生错误',
    retry: '重试',
    cancel: '取消',
    confirm: '确认',
    save: '保存',
    delete: '删除',
    edit: '编辑',
    search: '搜索',
  },
  workspace: {
    sidebar: {
      title: '工作空间',
      allTasks: '所有任务',
      projects: '项目',
      newProject: '新建项目',
      noProjects: '暂无项目',
      editProject: '编辑项目',
      deleteProject: '删除项目',
      expandSidebar: '展开侧栏',
      collapseSidebar: '收起侧栏',
    },
    toolbar: {
      searchPlaceholder: '搜索任务...',
      newTask: '新建',
      closeWorkspace: '关闭工作空间',
      showFilters: '显示筛选',
      hideFilters: '隐藏筛选',
      clearAll: '清除全部',
      listView: '列表视图',
      boardView: '看板视图',
    },
    filters: {
      allStatus: '全部状态',
      pending: '待处理',
      inProgress: '进行中',
      inReview: '验收',
      completed: '已完成',
      onHold: '暂停',
      blocked: '阻塞',
      cancelled: '已取消',
      allPriority: '全部优先级',
      urgent: '紧急',
      high: '高',
      medium: '中',
      low: '低',
      allDates: '全部日期',
      overdue: '已逾期',
      today: '今天',
      thisWeek: '本周',
      thisMonth: '本月',
    },
    groupBy: {
      status: '状态',
      priority: '优先级',
      dueDate: '截止日期',
      created: '创建时间',
      assignee: '负责人',
    },
    statusLabels: {
      pending: '待处理',
      in_progress: '进行中',
      in_review: '验收',
      on_hold: '暂停',
      blocked: '阻塞',
      completed: '已完成',
      cancelled: '已取消',
    },
    priorityLabels: {
      urgent: '紧急',
      critical: '关键',
      high: '高',
      medium: '中',
      low: '低',
    },
    typeLabels: {
      feature: '功能',
      bug: '缺陷',
      improvement: '优化',
      research: '调研',
      documentation: '文档',
      testing: '测试',
      deployment: '部署',
      maintenance: '维护',
    },
    dateFormat: {
      today: '今天',
      tomorrow: '明天',
      weekdays: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'],
      overdue: '逾期 {n} 天',
      am: '上午',
      pm: '下午',
    },
    list: {
      loadingTasks: '加载任务中...',
      noTasks: '暂无任务',
      noTasksMatch: '没有匹配的任务',
      tryAdjusting: '尝试调整搜索或筛选条件',
      createFirst: '创建你的第一个任务',
      unassigned: '未分配',
      noDueDate: '无截止日期',
      later: '以后',
      yesterday: '昨天',
      older: '更早',
    },
    board: {
      loadingBoard: '加载看板中...',
      noTasks: '无任务',
      toDo: '待办',
      inProgress: '进行中',
      review: '验收',
      done: '已完成',
      unassigned: '未分配',
    },
    drawer: {
      title: '标题',
      details: '详情',
      comments: '评论',
      description: '描述',
      information: '信息',
      project: '项目',
      type: '类型',
      priority: '优先级',
      created: '创建时间',
      due: '截止时间',
      estimate: '预估天数(自己参考)',
      daysUnit: '天',
      assigneeLabel: '负责人',
      tags: '标签',
      overdueBadge: '已逾期',
      noComments: '暂无评论',
      writeComment: '写评论...',
      commentBtn: '评论',
      sending: '发送中...',
      confirmDelete: '确认删除',
    },
    members: {
      title: '成员',
      manageMembers: '管理成员',
      invite: '邀请',
      inviteEmail: '邮箱',
      inviteRole: '角色',
      inviteSend: '发送邀请',
      inviteSending: '发送中…',
      roleViewer: '只读',
      roleMember: '成员',
      roleManager: '管理员',
      roleAdmin: '管理员',
      roleOwner: '拥有者',
      list: '成员',
      empty: '暂无成员。邀请其他人加入协作。',
      you: '你',
      remove: '移除',
      confirmRemove: '确认移除这名成员?',
      changeRoleTitle: '调整角色',
      loadFailed: '加载成员失败',
      inviteSucceeded: '邀请已发送',
      inviteFailed: '邀请失败',
    },
  },
  systemRegistry: {
    tabAgents: 'Agents / 工具',
    tabSkills: '技能',
    resyncButton: '一键同步全部',
    resyncing: '同步中...',
    columns: {
      id: 'ID',
      name: '名称',
      type: '类型',
      status: '状态',
      roles: '授权角色',
      synced: '最近同步',
      actions: '操作',
    },
    statusActive: '启用',
    statusDeprecated: '已弃用',
    revokeAll: '批量撤销',
    revoking: '撤销中...',
    revokeConfirmTitle: '确认批量撤销权限?',
    revokeConfirmBody: '将从所有已授权的角色中移除「{id}」的使用权限。此操作无法从 UI 撤回。',
    revokeConfirmAction: '确认撤销',
    revokeCancel: '取消',
    emptyAgents: '没有注册的 Agent/工具。',
    emptySkills: '没有注册的可执行技能。',
    loading: '正在加载...',
    loadFailed: '加载失败',
    syncFailed: '同步失败',
    revokeFailed: '撤销失败',
  },
  chat: {
    disclaimer: 'Datacenter AI 可能会出错，请核对重要信息。如有疑问，请联系数据中心。',
  },
};

// ---------------------------------------------------------------------------
// Accessor
// ---------------------------------------------------------------------------

const translations: Record<Language, GlobalTranslations> = { en, zh };

export function getTranslation(language: Language): GlobalTranslations {
  return translations[language] || translations.en;
}
